import axios from "axios";
import config from "../../config.json";

export const getOrganizationId = () => {
  const storedData = JSON.parse(localStorage.getItem("userState"));
  return storedData?.user?.organizationId;
};

// Use the global axios instance so shared interceptors (auth) are applied.
axios.defaults.baseURL = config.baseURL;
axios.defaults.withCredentials = true;
const axiosInstance = axios;

// Helper function to save params to localStorage
const saveReportParams = (params, reportType) => {
  try {
    const reportParams = {
      organizationId: params.organizationId,
      dateOption: params.dateOption,
      allVehicles: params.allVehicles || false,
      allRoutes: params.allRoutes || false,
      allDrivers: params.allDrivers || false,
      allStops: params.allStops || false,
      allSites: params.allSites || false,
      reportType: reportType,
      timestamp: Date.now()
    };
    localStorage.setItem('reportCategoryParams', JSON.stringify(reportParams));
    console.log('Saved report params:', reportParams);
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

// Single category report logic - query only one category at a time
async function generateSingleCategoryParams({
  orgId,
  siteId,
  category, // 'drivers' | 'vehicles' | 'routes' | 'stops' | 'users'
  selectedIds = [], // Array of selected IDs for the chosen category
  selectAll = false, // Whether to select all items in the category
  dateOption,
  customStartDate,
  customEndDate,
}) {
  const baseParams = {
    organizationId: orgId,
    siteId,
    dateOption,
    customStartDate,
    customEndDate,
  };

  // Initialize all category params as false/undefined
  let categoryParams = {
    deviceIds: undefined,
    routeIds: undefined,
    driverIds: undefined,
    stopIds: undefined,
    allDevices: false,
    allVehicles: false,
    allRoutes: false,
    allDrivers: false,
    allStops: false,
    allSites: false, // Set to false since we're filtering by category
  };

  // Set parameters based on selected category
  switch (category) {
    case "devices":
      if (selectAll) {
        categoryParams.allDevices = true;
      } else {
        categoryParams.deviceIds = selectedIds?.join(",");
        categoryParams.allDevices = false;
      }
      break;

    case "vehicles":
      if (selectAll) {
        categoryParams.allVehicles = true;
      } else {
        categoryParams.deviceIds = selectedIds?.join(",");
        categoryParams.allVehicles = false;
      }
      break;

    case "drivers":
      if (selectAll) {
        categoryParams.allDrivers = true;
      } else {
        categoryParams.driverIds = selectedIds?.join(",");
        categoryParams.allDrivers = false;
      }
      break;

    case "routes":
      if (selectAll) {
        categoryParams.allRoutes = true;
      } else {
        categoryParams.routeIds = selectedIds?.join(",");
        categoryParams.allRoutes = false;
      }
      break;

    case "stops":
      if (selectAll) {
        categoryParams.allStops = true;
      } else {
        categoryParams.stopIds = selectedIds?.join(",");
        categoryParams.allStops = false;
      }
      break;

    case "users":
      if (selectAll) {
        categoryParams.allUsers = true;
      } else {
        categoryParams.userIds = selectedIds?.join(",");
        categoryParams.allUsers = false;
      }
      break;

    default:
      throw new Error(`Invalid category: ${category}`);
  }

  return {
    ...baseParams,
    ...categoryParams,
    ...(category === "devices" ? { reportType: "devices" } : {}),
  };
}

export const reportsApi = {
  getDriversByOrganization: async (organizationId = null) => {
    try {

      const orgId = organizationId || getOrganizationId();
      const response = await axiosInstance.get("/driver/organization", {
        params: { organizationId: orgId },
      });
      console.log(response.data.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching drivers:", error);
      throw error;
    }
  },

  getRoutesByOrganization: async (organizationId = null) => {
    try {

      const orgId = organizationId || getOrganizationId();
      const response = await axiosInstance.get("/route/organization", {
        params: { organizationId: orgId },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching routes:", error);
      throw error;
    }
  },

  getSitesByOrganization: async (organizationId = null) => {
    try {

      const orgId = organizationId || getOrganizationId();
      const response = await axiosInstance.get("/site/organization", {
        params: { organizationId: orgId },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching sites:", error);
      throw error;
    }
  },

  getVehiclesByOrganization: async (organizationId = null) => {
    try {

      const orgId = organizationId || getOrganizationId();
      const response = await axiosInstance.get("/vehicle/organization", {
        params: { organizationId: orgId },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      throw error;
    }
  },

  getStopsByOrganization: async (organizationId = null) => {
    try {

      const orgId = organizationId || getOrganizationId();
      const response = await axiosInstance.get("/stop/organization", {
        params: { organizationId: orgId },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching stops:", error);
      throw error;
    }
  },

  getAllUsersByOrganization: async (organizationId = null, setIsLoading = () => { }) => {
    try {

      setIsLoading(true);
      const orgId = organizationId || getOrganizationId();
      const response = await axiosInstance.get(`/user/organization/${orgId}`);
      return response.data;
    } catch (error) {
      console.error("Error in fetching user data: ", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  },

  getTrackingDevicesByOrganization: async (organizationId = null) => {
    try {
      const orgId = organizationId || getOrganizationId();
      const response = await axiosInstance.get(`/device/organization/${orgId}`);
      const devices = response.data?.data || response.data?.devices || response.data;
      return Array.isArray(devices) ? devices : [];
    } catch (error) {
      console.error("Error fetching tracking devices:", error);
      return [];
    }
  },

  // get overspeed report function
  getOverspeedReport: async ({
    orgId,
    siteId,
    category,
    selectedIds,
    selectAll,
    dateOption,
    customStartDate,
    customEndDate,
  }) => {
    try {

      const params = await generateSingleCategoryParams({
        orgId,
        siteId,
        category,
        selectedIds,
        selectAll,
        dateOption,
        customStartDate,
        customEndDate,
      });

      saveReportParams(params, 'overspeed');

      const response = await axiosInstance.get("/report/overspeed", {
        params,
        paramsSerializer: (params) => {
          const searchParams = new URLSearchParams();
          for (const [key, value] of Object.entries(params)) {
            if (value !== undefined && value !== null && value !== "") {
              searchParams.append(key, value);
            }
          }
          return searchParams.toString();
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching overspeed report:", error);
      throw error;
    }
  },
  getCombinedActivityReport: async ({
    orgId,
    siteId,
    dateOption,
    customStartDate,
    customEndDate,
    sections = [],
  }) => {
    const params = {
      organizationId: orgId,
      siteId,
      dateOption,
      customStartDate,
      customEndDate,
      reportTypes: sections.map((section) => section.category).join(","),
    };

    sections.forEach((section) => {
      if (section.category === "vehicles") {
        params.allVehicles = section.selectAll;
        if (!section.selectAll) params.deviceIds = section.selectedIds.join(",");
      }
      if (section.category === "routes") {
        params.allRoutes = section.selectAll;
        if (!section.selectAll) params.routeIds = section.selectedIds.join(",");
      }
      if (section.category === "drivers") {
        params.allDrivers = section.selectAll;
        if (!section.selectAll) params.driverIds = section.selectedIds.join(",");
      }
      if (section.category === "stops") {
        params.allStops = section.selectAll;
        if (!section.selectAll) params.stopIds = section.selectedIds.join(",");
      }
    });

    const response = await axiosInstance.get("/report/activity", {
      params,
      paramsSerializer: (requestParams) => {
        const searchParams = new URLSearchParams();
        for (const [key, value] of Object.entries(requestParams)) {
          if (value !== undefined && value !== null && value !== "") {
            searchParams.append(key, value);
          }
        }
        return searchParams.toString();
      },
    });
    return response.data;
  },

  // get activity report
  getActivityReport: async ({
    orgId,
    siteId,
    category,
    selectedIds,
    selectAll,
    dateOption,
    customStartDate,
    customEndDate,
  }) => {
    try {
    

      const params = await generateSingleCategoryParams({
        orgId,
        siteId,
        category,
        selectedIds,
        selectAll,
        dateOption,
        customStartDate,
        customEndDate,
      });

      saveReportParams(params, 'activity');

      const response = await axiosInstance.get("/report/activity", {
        params,
        paramsSerializer: (params) => {
          const searchParams = new URLSearchParams();
          for (const [key, value] of Object.entries(params)) {
            if (value !== undefined && value !== null && value !== "") {
              searchParams.append(key, value);
            }
          }
          return searchParams.toString();
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching activity report:", error);
      throw error;
    }
  },

  // get SOS report
  getSosReport: async ({
    orgId,
    siteId,
    category,
    selectedIds,
    selectAll,
    dateOption,
    customStartDate,
    customEndDate,
  }) => {
    try {
   
      const params = await generateSingleCategoryParams({
        orgId,
        siteId,
        category,
        selectedIds,
        selectAll,
        dateOption,
        customStartDate,
        customEndDate,
      });

      saveReportParams(params, 'sos');

      const response = await axiosInstance.get("/report/sos", {
        params,
        paramsSerializer: (params) => {
          const searchParams = new URLSearchParams();
          for (const [key, value] of Object.entries(params)) {
            if (value !== undefined && value !== null && value !== "") {
              searchParams.append(key, value);
            }
          }
          return searchParams.toString();
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching SOS report:", error);
      throw error;
    }
  },

  // get inventory report
  getInventoryReport: async ({
    orgId,
    siteId,
    category,
    selectedIds,
    selectAll,
    dateOption,
    customStartDate,
    customEndDate,
  }) => {
    try {
     
      const params = await generateSingleCategoryParams({
        orgId,
        siteId,
        category,
        selectedIds,
        selectAll,
        dateOption,
        customStartDate,
        customEndDate,
      });

      saveReportParams(params, 'inventory');

      const response = await axiosInstance.get("/report/inventory", {
        params,
        paramsSerializer: (params) => {
          const searchParams = new URLSearchParams();
          for (const [key, value] of Object.entries(params)) {
            if (value !== undefined && value !== null && value !== "") {
              searchParams.append(key, value);
            }
          }
          return searchParams.toString();
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching inventory report:", error);
      throw error;
    }
  },

  // get runtime report
  getRuntimeReport: async ({
    orgId,
    siteId,
    category,
    selectedIds,
    selectAll,
    dateOption,
    customStartDate,
    customEndDate,
  }) => {
    try {
     
      const params = await generateSingleCategoryParams({
        orgId,
        siteId,
        category,
        selectedIds,
        selectAll,
        dateOption,
        customStartDate,
        customEndDate,
      });

      saveReportParams(params, 'runtime');

      const response = await axiosInstance.get("/report/runtime", {
        params,
        paramsSerializer: (params) => {
          const searchParams = new URLSearchParams();
          for (const [key, value] of Object.entries(params)) {
            if (value !== undefined && value !== null && value !== "") {
              searchParams.append(key, value);
            }
          }
          return searchParams.toString();
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching runtime report:", error);
      throw error;
    }
  },

  // get trip report
  getTripReport: async ({
    orgId,
    siteId,
    category,
    selectedIds,
    selectAll,
    dateOption,
    customStartDate,
    customEndDate,
  }) => {
    try {
      const params = await generateSingleCategoryParams({
        orgId,
        siteId,
        category,
        selectedIds,
        selectAll,
        dateOption,
        customStartDate,
        customEndDate,
      });

      saveReportParams(params, 'trip');

      const response = await axiosInstance.get("/report/trip", {
        params,
        paramsSerializer: (params) => {
          const searchParams = new URLSearchParams();
          for (const [key, value] of Object.entries(params)) {
            if (value !== undefined && value !== null && value !== "") {
              searchParams.append(key, value);
            }
          }
          return searchParams.toString();
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching trip report:", error);
      throw error;
    }
  },

  // get route deviation report
  getRouteDeviationReport: async ({
    orgId,
    siteId,
    category,
    selectedIds,
    selectAll,
    dateOption,
    customStartDate,
    customEndDate,
  }) => {
    try {
      const params = await generateSingleCategoryParams({
        orgId,
        siteId,
        category,
        selectedIds,
        selectAll,
        dateOption,
        customStartDate,
        customEndDate,
      });
      saveReportParams(params, 'deviation');
      const response = await axiosInstance.get('/report/deviation', {
        params,
        paramsSerializer: (params) => {
          const searchParams = new URLSearchParams();
          for (const [key, value] of Object.entries(params)) {
            if (value !== undefined && value !== null && value !== "") {
              searchParams.append(key, value);
            }
          }
          return searchParams.toString();
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching route deviation report:", error);
      throw error;
    }
  },

  // get getHalt Report report
  getHaltReport: async ({
    orgId,
    siteId,
    category,
    selectedIds,
    selectAll,
    dateOption,
    customStartDate,
    customEndDate,
  }) => {
    try {
      const params = await generateSingleCategoryParams({
        orgId,
        siteId,
        category,
        selectedIds,
        selectAll,
        dateOption,
        customStartDate,
        customEndDate,
      });

      saveReportParams(params, 'halt');

      const response = await axiosInstance.get("/report/halt", {
        params,
        paramsSerializer: (params) => {
          const searchParams = new URLSearchParams();
          for (const [key, value] of Object.entries(params)) {
            if (value !== undefined && value !== null && value !== "") {
              searchParams.append(key, value);
            }
          }
          return searchParams.toString();
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching route deviation report:", error);
      throw error;
    }
  },

  // get Entry Exit Report report
  getEntryExitReport: async ({
    orgId,
    siteId,
    category,
    selectedIds,
    selectAll,
    dateOption,
    customStartDate,
    customEndDate,
  }) => {
    try {
      const params = await generateSingleCategoryParams({
        orgId,
        siteId,
        category,
        selectedIds,
        selectAll,
        dateOption,
        customStartDate,
        customEndDate,
      });

      saveReportParams(params, 'entry-exit');

      const response = await axiosInstance.get("/report/entry-exist", {
        params,
        paramsSerializer: (params) => {
          const searchParams = new URLSearchParams();
          for (const [key, value] of Object.entries(params)) {
            if (value !== undefined && value !== null && value !== "") {
              searchParams.append(key, value);
            }
          }
          return searchParams.toString();
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching route deviation report:", error);
      throw error;
    }
  },

  getAuditLog: async ({
    actorUserId,
    action,
    dateOption,
    customStartDate,
    customEndDate,
  }) => {
    const response = await axiosInstance.get("/report/audit", {
      params: {
        actorUserId: actorUserId || undefined,
        action: action || undefined,
        dateOption,
        customStartDate: customStartDate || undefined,
        customEndDate: customEndDate || undefined,
      },
    });
    return response.data;
  },

  // Helper function to normalize vehicle status
  // Converts status to "active" if ACTIVE, otherwise "inactive"
  normalizeVehicleStatus: (vehicles) => {
    if (!Array.isArray(vehicles)) {
      return vehicles;
    }
    
    return vehicles.map(vehicle => ({
      ...vehicle,
      status: vehicle.status === "ACTIVE" ? "active" : "inactive"
    }));
  },

  // Get vehicles with normalized status
  getVehiclesByOrganizationWithStatus: async (organizationId = null) => {
    try {
      const orgId = organizationId || getOrganizationId();
      const response = await axiosInstance.get("/vehicle/organization", {
        params: { organizationId: orgId },
      });
      
      // Normalize the vehicle status data
      const vehiclesWithStatus = reportsApi.normalizeVehicleStatus(response.data);
      return vehiclesWithStatus;
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      throw error;
    }
  },


};
